import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, MailCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { cn } from "@/platform/lib/cn";
import { resendSignupOtp, verifySignupOtp } from "@/platform/auth/api";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // segundos

/**
 * `/verificar` — verificação de conta por código OTP de 6 dígitos.
 *
 * Recebe ?email= e ?tenant= (query). Após verificar, estabelece sessão e
 * redireciona para /precos?tenant=… para escolha de plano + checkout Stripe
 * (cartão obrigatório antes do trial). O onboarding vem após a assinatura.
 *
 * Pré-requisito Supabase: template "Confirm signup" usando {{ .Token }}.
 */
export function VerifyOtpPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") ?? "";
  const tenant = params.get("tenant") === "usp" ? "usp" : "einstein";

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (!email) {
      toast.error("Email não informado. Refaça o cadastro.");
      navigate("/cadastro", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  function setDigit(idx: number, value: string) {
    const clean = value.replace(/\D/g, "");
    if (clean.length > 1) {
      // colou o código inteiro
      const arr = clean.slice(0, OTP_LENGTH).split("");
      const next = Array(OTP_LENGTH).fill("");
      arr.forEach((d, i) => (next[i] = d));
      setDigits(next);
      const lastFilled = Math.min(arr.length, OTP_LENGTH) - 1;
      inputsRef.current[lastFilled]?.focus();
      if (arr.length >= OTP_LENGTH) void submit(next.join(""));
      return;
    }
    const next = [...digits];
    next[idx] = clean;
    setDigits(next);
    if (clean && idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
    if (next.every((d) => d) && next.join("").length === OTP_LENGTH) {
      void submit(next.join(""));
    }
  }

  function onKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  }

  async function submit(code: string) {
    if (code.length !== OTP_LENGTH || submitting) return;
    setSubmitting(true);
    try {
      await verifySignupOtp(email, code);
      // Cartão obrigatório antes do trial: tenant ou não, manda para escolher
      // plano + checkout. O webhook ativa a assinatura e libera o app.
      toast.success("Conta verificada. Escolha seu plano para começar.");
      navigate(`/precos?tenant=${tenant === "usp" ? "usp" : "einstein"}`, { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Código inválido ou expirado.");
      setDigits(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  async function resend() {
    if (cooldown > 0) return;
    try {
      await resendSignupOtp(email);
      toast.success("Código reenviado. Confira seu email.");
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível reenviar.");
    }
  }

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="violet" className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob" />
      <SoftStar color="yellow" className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float" />

      <Container size="default" className="relative flex min-h-[calc(100vh-56px)] flex-col items-center justify-center py-16">
        <div className="w-full max-w-md text-center animate-fade-up">
          <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-[color:var(--color-accent-soft)]">
            <MailCheck className="size-7 text-[color:var(--color-accent)]" aria-hidden />
          </span>
          <Badge variant="accentSolid" className="mt-5">
            Verificação de conta
          </Badge>
          <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Digite o código de 6 dígitos.
          </h1>
          <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            Enviamos um código para <strong>{email}</strong>. Cole ou digite abaixo.
          </p>

          <div className="mt-8 flex justify-center gap-2">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => { inputsRef.current[i] = el; }}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={OTP_LENGTH}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                disabled={submitting}
                aria-label={`Dígito ${i + 1}`}
                className={cn(
                  "size-12 rounded-xl border-2 text-center font-display text-[length:var(--text-h2)] font-semibold tabular-nums transition-colors focus:outline-none",
                  d ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)]" : "border-[--color-border] bg-[color:var(--color-card)]",
                  "focus:border-[color:var(--color-accent)] focus:ring-2 focus:ring-[color:var(--color-accent)]/30",
                )}
              />
            ))}
          </div>

          <Button
            size="lg"
            variant="gradient"
            className="mt-8 w-full"
            loading={submitting}
            disabled={digits.join("").length !== OTP_LENGTH}
            onClick={() => submit(digits.join(""))}
          >
            Verificar
            <ArrowUpRight className="size-4" aria-hidden />
          </Button>

          <div className="mt-6 flex flex-col items-center gap-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            <button
              type="button"
              onClick={resend}
              disabled={cooldown > 0}
              className="inline-flex items-center gap-1.5 font-medium text-[color:var(--color-accent)] transition-colors hover:text-[color:var(--color-accent-deep)] disabled:text-[color:var(--color-muted-foreground)]"
            >
              <RotateCcw className="size-3.5" aria-hidden />
              {cooldown > 0 ? `Reenviar em ${cooldown}s` : "Reenviar código"}
            </button>
            <Link to="/cadastro" className="link">
              Usar outro email
            </Link>
          </div>
        </div>
      </Container>
    </div>
  );
}
